import { useEffect, useMemo, useState } from "react";
import {
  AlertCircle,
  CheckCircle2,
  ClipboardCheck,
  ExternalLink,
  FileText,
  Lock,
  Printer,
  Send,
  Undo2,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/cn";
import { formatDate } from "@/lib/format";
import { useBrief, useUpsertBrief, useBriefAction, type Brief } from "@/hooks/useBrief";
import { useUpdateProject } from "@/hooks/usePortfolio";
import { PersonPicker } from "@/components/ui/PersonPicker";
import { RichTextEditor } from "@/components/ui/RichTextEditor";
import { RichText } from "@/components/ui/RichText";
import type { Project } from "@/types/project";

// ---------------------------------------------------------------------------
// Static config
// ---------------------------------------------------------------------------

const STRATEGIC_ANCHORS = [
  { value: "production_ops", label: "Production & Ops" },
  { value: "sales_distribution", label: "Sales & Distribution" },
  { value: "people_hr", label: "People & HR" },
  { value: "technology_systems", label: "Technology & Systems" },
  { value: "financial", label: "Financial" },
  { value: "market", label: "Market" },
];

const IT_NEEDED_OPTIONS = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
  { value: "unknown", label: "I don't know" },
];

const TSHIRT_SIZES = ["S", "M", "L", "XL", "XXL"];

// 37 items across 6 categories. Key is stable (used in JSON storage).
export const IT_CHECKLIST_CATEGORIES: Array<{
  category: string;
  items: Array<{ key: string; label: string }>;
}> = [
  {
    category: "Applications",
    items: [
      { key: "salesforce", label: "Salesforce" },
      { key: "buyete", label: "BuyETE" },
      { key: "etesync", label: "ETESync" },
      { key: "integration", label: "Integration" },
      { key: "edi", label: "EDI" },
      { key: "power_bi", label: "Power BI (Dashboards)" },
      { key: "infor_syteline", label: "INFOR — Syteline" },
      { key: "label_printing", label: "Label Printing" },
      { key: "labview", label: "Labview" },
      { key: "pickpro", label: "PickPro" },
      { key: "vks", label: "VKS" },
      { key: "new_software_licensing", label: "New Software — Licensing" },
    ],
  },
  {
    category: "Network",
    items: [
      { key: "physical_internet", label: "Physical Internet Connection (Access Drop)" },
      { key: "wireless_infra", label: "Wireless Infrastructure" },
      { key: "bluetooth", label: "Bluetooth" },
      { key: "peer_to_peer", label: "Peer-to-Peer" },
    ],
  },
  {
    category: "Security",
    items: [
      { key: "remote_access", label: "Remote Access" },
      { key: "soc2", label: "New Software SOC 2" },
      { key: "whitelist", label: "Whitelist" },
      { key: "mfa", label: "Multi-Factor Authentication" },
    ],
  },
  {
    category: "Infrastructure",
    items: [
      { key: "servers", label: "Servers" },
      { key: "server_specs", label: "Server Specs" },
      { key: "data_storage", label: "Data Storage" },
      { key: "cabling", label: "Cabling" },
      { key: "resilience_ha", label: "Resilience & High Availability" },
    ],
  },
  {
    category: "Peripherals",
    items: [
      { key: "ipads", label: "iPads" },
      { key: "printers_scanners", label: "Label / Laser Printers / Scanners / MFDs" },
      { key: "laptop", label: "Laptop" },
      { key: "mobile_cards", label: "Mobile cards" },
      { key: "desktops", label: "Desktops" },
      { key: "power_ups", label: "Power distribution UPS" },
      { key: "monitors_etc", label: "Monitors / Keyboards / Mice / Docking" },
    ],
  },
  {
    category: "Support",
    items: [
      { key: "ete_it", label: "ETE IT" },
      { key: "ete_business", label: "ETE Business" },
      { key: "third_party_remote", label: "3rd Party — remote" },
      { key: "third_party_onsite", label: "3rd Party — onsite" },
      { key: "internal_maintenance", label: "Internal Maintenance & Support" },
    ],
  },
];

// Sitewide status colors: gray (not in progress) / sky (in progress) /
// emerald (done). 'submitted' is mid-workflow → sky.
const STATUS_PILL: Record<string, string> = {
  not_required: "bg-raised text-ink-muted",
  not_started: "bg-raised text-ink-3",
  in_progress: "bg-sky-100 dark:bg-sky-500/15 text-sky-700 dark:text-sky-300",
  submitted: "bg-sky-100 dark:bg-sky-500/15 text-sky-700 dark:text-sky-300",
  approved: "bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
  declined: "bg-raised text-ink-muted",
};

const STATUS_LABEL: Record<string, string> = {
  not_required: "Not Required",
  not_started: "Not Started",
  in_progress: "In Progress",
  submitted: "Submitted",
  approved: "Approved",
  declined: "Declined",
};

// Fields that count for the "% complete" progress indicator
const PROGRESS_FIELDS: Array<keyof Brief> = [
  "requester_name",
  "requester_department",
  "primary_contact",
  "business_need",
  "strategic_anchor",
  "target_kpi",
  "in_scope",
  "out_of_scope",
  "success_criteria",
  "business_owner",
  "key_departments",
  "expected_start",
  "key_milestones",
  "top_risks",
  "dependencies",
  "it_needed",
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface Props {
  projectId: string;
  project: Project | null | undefined;
}

export function BriefPanel({ projectId, project }: Props) {
  const briefQ = useBrief(projectId);
  const upsert = useUpsertBrief(projectId);
  // Sponsor lives on the project record, not the Brief — but the
  // Brief surfaces it for context. Edit-in-place on this field saves
  // back to the project so the value stays consistent everywhere
  // (Overview, Initiative inheritance, etc.).
  const updateProject = useUpdateProject();
  const action = useBriefAction(projectId);

  const brief = briefQ.data;

  if (briefQ.isLoading) {
    return (
      <div className="rounded-xl border border-line bg-surface p-12 text-center text-sm text-ink-muted">
        Loading brief...
      </div>
    );
  }

  if (!brief) {
    return (
      <div className="rounded-xl border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10 p-6 text-sm text-red-800 dark:text-red-300">
        Couldn't load brief. {(briefQ.error as Error)?.message}
      </div>
    );
  }

  const locked = brief.status === "approved" || brief.status === "declined";
  const isSubmitted = brief.status === "submitted";
  const needsBrief = brief.status !== "not_required";

  const completePct = (() => {
    const filled = PROGRESS_FIELDS.filter((k) => {
      const v = brief[k];
      return v !== null && v !== undefined && String(v).trim().length > 0;
    }).length;
    return Math.round((filled / PROGRESS_FIELDS.length) * 100);
  })();

  const handleRequire = (required: boolean) => {
    action.mutate(
      { action: "require", payload: { required } },
      {
        onSuccess: () =>
          toast.success(required ? "Brief required for this project." : "Brief marked not required."),
      },
    );
  };

  const handleSubmit = () => {
    if (completePct < 50) {
      if (!window.confirm(`The brief is only ${completePct}% filled in. Submit anyway?`)) return;
    }
    action.mutate(
      { action: "submit" },
      { onSuccess: () => toast.success("Brief submitted for review.") },
    );
  };

  // Offline path: brief was filled out on paper / PDF / email and just
  // needs to be marked received in the system. Same backend action as
  // Submit, but skips the "fill in the sections" confirmation since
  // there's nothing to fill in here — the source of truth lives offline.
  const handleMarkReceived = () => {
    action.mutate(
      { action: "submit" },
      { onSuccess: () => toast.success("Brief marked as received.") },
    );
  };

  const handleApprove = () => {
    const decidedBy = window.prompt("Who approved this? (for the audit log)", "");
    if (decidedBy === null) return;
    action.mutate(
      { action: "approve", payload: { decided_by: decidedBy || undefined } },
      { onSuccess: () => toast.success("Brief approved.") },
    );
  };

  const handleDecline = () => {
    const reason = window.prompt("Reason for decline (optional):", "");
    if (reason === null) return;
    const decidedBy = window.prompt("Who declined this? (for the audit log)", "");
    if (decidedBy === null) return;
    action.mutate(
      { action: "decline", payload: { decided_by: decidedBy || undefined, reason: reason || undefined } },
      { onSuccess: () => toast.success("Brief declined.") },
    );
  };

  const handleReopen = () => {
    action.mutate(
      { action: "reopen" },
      { onSuccess: () => toast.success("Brief reopened for editing.") },
    );
  };

  // Not-required state → show the "enable" prompt
  if (!needsBrief) {
    return (
      <div className="rounded-xl border border-line bg-surface p-8">
        <div className="flex items-start gap-3">
          <ClipboardCheck className="h-5 w-5 shrink-0 text-ink-subtle mt-0.5" />
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-ink">Kickstart Brief</h3>
            <p className="mt-1 text-xs text-ink-3 max-w-xl">
              Projects that need executive approval must have a Kickstart Brief filled out.
              If this project will go to the monthly approval meeting, click <strong>Require brief</strong>{" "}
              to start one.
            </p>
            <button
              onClick={() => handleRequire(true)}
              disabled={action.isPending}
              className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-brand-navy-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-navy-700 disabled:opacity-40 transition-colors"
            >
              <ClipboardCheck className="h-3.5 w-3.5" /> Require brief
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Active brief
  return (
    <div className="space-y-4">
      {/* Header bar */}
      <div className="rounded-xl border border-line bg-surface p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand-navy-50 dark:bg-brand-navy-500/10 text-brand-navy-700 dark:text-brand-navy-300">
              <ClipboardCheck className="h-4 w-4" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-semibold text-ink">Kickstart Brief</h3>
                <span className={cn("rounded-full px-2 py-0.5 text-[10px] font-semibold", STATUS_PILL[brief.status] ?? "bg-raised text-ink-muted")}>
                  {STATUS_LABEL[brief.status] ?? brief.status}
                </span>
              </div>
              <div className="mt-0.5 text-[11px] text-ink-muted">
                {brief.status === "not_started" && "Not started — fill in below, or mark received if filled out offline."}
                {brief.status === "in_progress" && `${completePct}% complete`}
                {brief.status === "submitted" && brief.submitted_at &&
                  `Submitted ${formatDate(brief.submitted_at)}`}
                {brief.status === "approved" && brief.decided_at && (
                  <>
                    Approved {formatDate(brief.decided_at)}
                    {brief.decided_by ? ` by ${brief.decided_by}` : ""}
                  </>
                )}
                {brief.status === "declined" && brief.decided_at && (
                  <>
                    Declined {formatDate(brief.decided_at)}
                    {brief.decided_by ? ` by ${brief.decided_by}` : ""}
                    {brief.decline_reason ? ` — ${brief.decline_reason}` : ""}
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <a
              href={`/portfolio/${projectId}/brief/print`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 rounded-lg border border-line bg-surface px-3 py-1.5 text-[11px] font-medium text-ink-3 hover:bg-raised transition-colors"
            >
              <Printer className="h-3 w-3" /> Export PDF
              <ExternalLink className="h-2.5 w-2.5 text-ink-subtle" />
            </a>
            {brief.status === "not_started" && (
              <button
                onClick={handleMarkReceived}
                disabled={action.isPending}
                className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-40 transition-colors"
                title="Brief was filled out offline — mark it as received without filling in the form."
              >
                <CheckCircle2 className="h-3 w-3" /> Mark brief received
              </button>
            )}
            {(brief.status === "not_started" || brief.status === "in_progress") && (
              <button
                onClick={handleSubmit}
                disabled={action.isPending}
                className="inline-flex items-center gap-1.5 rounded-lg bg-brand-navy-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-navy-700 disabled:opacity-40 transition-colors"
              >
                <Send className="h-3 w-3" /> Submit for review
              </button>
            )}
            {isSubmitted && (
              <>
                <button
                  onClick={handleDecline}
                  disabled={action.isPending}
                  className="inline-flex items-center gap-1 rounded-lg border border-red-200 dark:border-red-500/30 bg-surface px-3 py-1.5 text-[11px] font-semibold text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-500/15 transition-colors"
                >
                  <XCircle className="h-3 w-3" /> Decline
                </button>
                <button
                  onClick={handleApprove}
                  disabled={action.isPending}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-40 transition-colors"
                >
                  <CheckCircle2 className="h-3 w-3" /> Approve
                </button>
              </>
            )}
            {locked && (
              <button
                onClick={handleReopen}
                disabled={action.isPending}
                className="inline-flex items-center gap-1 rounded-lg border border-line bg-surface px-3 py-1.5 text-[11px] font-medium text-ink-3 hover:bg-raised transition-colors"
              >
                <Undo2 className="h-3 w-3" /> Reopen
              </button>
            )}
            <button
              onClick={() => handleRequire(false)}
              disabled={action.isPending || locked}
              className="inline-flex items-center gap-1 rounded-lg border border-line bg-surface px-3 py-1.5 text-[11px] font-medium text-ink-muted hover:bg-raised disabled:opacity-40 transition-colors"
              title="Mark this project as not needing a brief"
            >
              Not required
            </button>
          </div>
        </div>

        {/* Progress bar */}
        {(brief.status === "not_started" || brief.status === "in_progress") && (
          <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-raised">
            <div
              className="h-full rounded-full bg-brand-navy-500 transition-all"
              style={{ width: `${Math.max(2, completePct)}%` }}
            />
          </div>
        )}
      </div>

      {locked && (
        <div className="flex items-center gap-2 rounded-lg border border-line bg-canvas px-3 py-2 text-[11px] text-ink-3">
          <Lock className="h-3 w-3" />
          <span>This brief is {STATUS_LABEL[brief.status]} and read-only. Click Reopen to edit.</span>
        </div>
      )}

      {isSubmitted && (
        <div className="flex items-center gap-2 rounded-lg border border-sky-200 dark:border-sky-500/30 bg-sky-50/60 dark:bg-sky-500/10 px-3 py-2 text-[11px] text-sky-800 dark:text-sky-300">
          <AlertCircle className="h-3 w-3 shrink-0" />
          <span>Awaiting PMO review. Fields are editable until a decision is made.</span>
        </div>
      )}

      {/* Sections */}
      <BriefSections
        brief={brief}
        project={project}
        locked={locked}
        onSave={(patch) => upsert.mutate(patch)}
        onSaveProject={(patch) => {
          updateProject.mutate(
            { id: projectId, patch },
            {
              onError: (err) => toast.error((err as Error).message),
            },
          );
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sections
// ---------------------------------------------------------------------------

interface SectionsProps {
  brief: Brief;
  project: Project | null | undefined;
  locked: boolean;
  onSave: (patch: Partial<Brief>) => void;
  /** Save handler for project-record fields surfaced on the Brief
   *  (Sponsor — lives on Project, edited here for convenience). */
  onSaveProject: (patch: Partial<Project>) => void;
}

function BriefSections({ brief, project, locked, onSave, onSaveProject }: SectionsProps) {
  return (
    <>
      {/* Basic Info */}
      <Section title="Basic Info" hint="Who's asking, who's sponsoring, who to contact">
        <div className="grid gap-3 md:grid-cols-2">
          <ReadOnlyField label="Project Name" value={project?.name ?? brief.project_id} />
          <TextField
            label="Sponsor (VP/Director)"
            value={project?.sponsor ?? ""}
            placeholder="Sponsor name"
            disabled={locked}
            onSave={(v) => onSaveProject({ sponsor: v || null } as Partial<Project>)}
          />
          <TextField
            label="Requester & Department"
            value={brief.requester_name ?? ""}
            placeholder="Name, Department"
            disabled={locked}
            onSave={(v) => onSave({ requester_name: v || null })}
          />
          <PersonField
            label="Primary Contact"
            value={brief.primary_contact ?? ""}
            placeholder="Primary contact name"
            disabled={locked}
            onSave={(v) => onSave({ primary_contact: v || null })}
          />
        </div>
        <div className="mt-3">
          <TextField
            label="Requester Department (detail)"
            value={brief.requester_department ?? ""}
            placeholder="e.g. Operations, Finance, Sales"
            disabled={locked}
            onSave={(v) => onSave({ requester_department: v || null })}
          />
        </div>
      </Section>

      {/* Expected Cost vs Budget */}
      <Section title="Expected Cost vs Budget" hint="Rough numbers are fine — refine later">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-[10px] font-semibold uppercase tracking-wider text-ink-subtle">
                <th className="py-2 pr-3">Category</th>
                <th className="py-2 pr-3">Expected Cost</th>
                <th className="py-2 pr-3">$s Approved in Annual Budget</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-t border-line-soft">
                <td className="py-2 pr-3 font-medium text-ink-2">Capital Investment</td>
                <td className="py-2 pr-3">
                  <MoneyField
                    value={brief.expected_capital}
                    disabled={locked}
                    onSave={(v) => onSave({ expected_capital: v })}
                  />
                </td>
                <td className="py-2 pr-3">
                  <MoneyField
                    value={brief.budgeted_capital}
                    disabled={locked}
                    onSave={(v) => onSave({ budgeted_capital: v })}
                  />
                </td>
              </tr>
              <tr className="border-t border-line-soft">
                <td className="py-2 pr-3 font-medium text-ink-2">Other Expenses</td>
                <td className="py-2 pr-3">
                  <MoneyField
                    value={brief.expected_other}
                    disabled={locked}
                    onSave={(v) => onSave({ expected_other: v })}
                  />
                </td>
                <td className="py-2 pr-3">
                  <MoneyField
                    value={brief.budgeted_other}
                    disabled={locked}
                    onSave={(v) => onSave({ budgeted_other: v })}
                  />
                </td>
              </tr>
              <tr className="border-t border-line bg-canvas/50">
                <td className="py-2 pr-3 font-semibold text-ink">Total</td>
                <td className="py-2 pr-3 font-semibold tabular-nums text-ink">
                  {formatMoney((brief.expected_capital ?? 0) + (brief.expected_other ?? 0))}
                </td>
                <td className="py-2 pr-3 font-semibold tabular-nums text-ink">
                  {formatMoney((brief.budgeted_capital ?? 0) + (brief.budgeted_other ?? 0))}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </Section>

      {/* Purpose & Alignment */}
      <Section title="Purpose & Alignment" hint="Why this project, how it ties to strategy">
        <Textarea
          label="Business Need"
          hint="2–3 bullets explaining the problem or opportunity"
          value={brief.business_need ?? ""}
          placeholder="• What's broken or missing today?&#10;• Why does this matter now?&#10;• What happens if we don't act?"
          disabled={locked}
          rows={4}
          onSave={(v) => onSave({ business_need: v || null })}
        />
        <div className="mt-3 grid gap-3 md:grid-cols-2">
          <SelectField
            label="Strategic Anchor"
            value={brief.strategic_anchor ?? ""}
            options={STRATEGIC_ANCHORS}
            disabled={locked}
            onSave={(v) => onSave({ strategic_anchor: v || null })}
          />
        </div>
        <div className="mt-3">
          <Textarea
            label="Target KPI / Outcome"
            hint="How will we know this was worth doing?"
            value={brief.target_kpi ?? ""}
            placeholder="e.g. Reduce order-to-ship time by 25%..."
            disabled={locked}
            rows={2}
            onSave={(v) => onSave({ target_kpi: v || null })}
          />
        </div>
      </Section>

      {/* Scope & Success Criteria */}
      <Section title="Scope & Success Criteria" hint="What's in, what's out, and how we'll know we won">
        <Textarea
          label="In Scope"
          value={brief.in_scope ?? ""}
          placeholder="What's included in this project..."
          disabled={locked}
          rows={3}
          onSave={(v) => onSave({ in_scope: v || null })}
        />
        <div className="mt-3">
          <Textarea
            label="Out of Scope"
            value={brief.out_of_scope ?? ""}
            placeholder="What's NOT included — explicitly..."
            disabled={locked}
            rows={3}
            onSave={(v) => onSave({ out_of_scope: v || null })}
          />
        </div>
        <div className="mt-3">
          <Textarea
            label="Success Criteria"
            value={brief.success_criteria ?? ""}
            placeholder="Observable, measurable outcomes that signal success..."
            disabled={locked}
            rows={3}
            onSave={(v) => onSave({ success_criteria: v || null })}
          />
        </div>
      </Section>

      {/* Stakeholders */}
      <Section title="Stakeholders" hint="Who's leading, who needs to be involved">
        <div className="grid gap-3 md:grid-cols-2">
          <PersonField
            label="Business Owner"
            hint="Who will lead the project/activities?"
            value={brief.business_owner ?? ""}
            placeholder="Name"
            disabled={locked}
            onSave={(v) => onSave({ business_owner: v || null })}
          />
        </div>
        <div className="mt-3">
          <Textarea
            label="Key Departments / Partners"
            value={brief.key_departments ?? ""}
            placeholder="Departments, teams, external partners..."
            disabled={locked}
            rows={2}
            onSave={(v) => onSave({ key_departments: v || null })}
          />
        </div>
      </Section>

      {/* Timeline */}
      <Section title="Timeline" hint="When does this start, what are the key checkpoints">
        <div className="grid gap-3 md:grid-cols-2">
          <DateField
            label="Expected Start"
            value={brief.expected_start ?? ""}
            disabled={locked}
            onSave={(v) => onSave({ expected_start: v || null })}
          />
        </div>
        <div className="mt-3">
          <Textarea
            label="Key Milestones"
            value={brief.key_milestones ?? ""}
            placeholder="Major checkpoints and target dates if known..."
            disabled={locked}
            rows={3}
            onSave={(v) => onSave({ key_milestones: v || null })}
          />
        </div>
      </Section>

      {/* Risks & Dependencies */}
      <Section title="Risks & Dependencies" hint="What could go wrong, what does this depend on">
        <Textarea
          label="Top Risks"
          value={brief.top_risks ?? ""}
          placeholder="Significant risks or unknowns..."
          disabled={locked}
          rows={3}
          onSave={(v) => onSave({ top_risks: v || null })}
        />
        <div className="mt-3">
          <Textarea
            label="Dependencies"
            value={brief.dependencies ?? ""}
            placeholder="Other projects, systems, or decisions we rely on..."
            disabled={locked}
            rows={3}
            onSave={(v) => onSave({ dependencies: v || null })}
          />
        </div>
      </Section>

      {/* IT Involvement */}
      <Section title="IT Involvement" hint="Does this project touch IT systems?">
        <div className="grid gap-3 md:grid-cols-3">
          <SelectField
            label="IT Needed?"
            value={brief.it_needed ?? ""}
            options={IT_NEEDED_OPTIONS}
            disabled={locked}
            onSave={(v) => onSave({ it_needed: v || null })}
          />
          <SelectField
            label="T-Shirt Size (IT estimate)"
            value={brief.tshirt_size ?? ""}
            options={TSHIRT_SIZES.map((s) => ({ value: s, label: s }))}
            disabled={locked}
            onSave={(v) => onSave({ tshirt_size: v || null })}
          />
          <TextField
            label="Cost Range (IT estimate)"
            value={brief.cost_range ?? ""}
            placeholder="e.g. $10K–$50K"
            disabled={locked}
            onSave={(v) => onSave({ cost_range: v || null })}
          />
        </div>
      </Section>

      {/* IT Impact Checklist */}
      <Section
        title="IT Impact Checklist"
        hint="Check every area this project might touch (even if unsure)"
      >
        <ITChecklist
          selected={brief.it_checklist}
          disabled={locked}
          onChange={(next) => onSave({ it_checklist: next })}
        />
      </Section>

      {/* Other Info + Next Steps */}
      <Section title="Other Info & Next Steps">
        <Textarea
          label="Other Relevant Info"
          value={brief.other_info ?? ""}
          placeholder="Anything else worth knowing..."
          disabled={locked}
          rows={3}
          onSave={(v) => onSave({ other_info: v || null })}
        />
        <div className="mt-3">
          <TextField
            label="Available dates for intake discussion"
            value={brief.intake_discussion_dates ?? ""}
            placeholder="e.g. Tue 4/22, Thu 4/24 afternoon"
            disabled={locked}
            onSave={(v) => onSave({ intake_discussion_dates: v || null })}
          />
        </div>
      </Section>
    </>
  );
}

// ---------------------------------------------------------------------------
// Section wrapper
// ---------------------------------------------------------------------------

function Section({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border border-line bg-surface p-5">
      <div className="mb-3">
        <div className="flex items-center gap-2">
          <FileText className="h-3.5 w-3.5 text-brand-navy-500" />
          <h4 className="text-sm font-semibold text-ink">{title}</h4>
        </div>
        {hint && <p className="mt-0.5 text-[11px] text-ink-muted">{hint}</p>}
      </div>
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Field primitives (each saves on blur)
// ---------------------------------------------------------------------------

const inputCls =
  "w-full rounded-md border border-line bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-subtle focus:border-brand-navy-400 focus:outline-none focus:ring-2 focus:ring-brand-navy-100 disabled:bg-canvas disabled:text-ink-muted";

const labelCls =
  "block text-[10px] font-semibold uppercase tracking-wider text-ink-muted mb-1";

function ReadOnlyField({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className={labelCls}>{label}</div>
      <div className="rounded-md border border-line bg-canvas/60 px-3 py-2 text-sm text-ink-2">
        {value}
      </div>
    </div>
  );
}

function TextField({
  label,
  hint,
  value,
  placeholder,
  disabled,
  onSave,
}: {
  label: string;
  hint?: string;
  value: string;
  placeholder?: string;
  disabled?: boolean;
  onSave: (v: string) => void;
}) {
  const [local, setLocal] = useState(value);
  useEffect(() => setLocal(value), [value]);
  return (
    <label className="block">
      <div className={labelCls}>
        {label}
        {hint && <span className="ml-2 normal-case text-ink-subtle font-normal">{hint}</span>}
      </div>
      <input
        type="text"
        value={local}
        onChange={(e) => setLocal(e.target.value)}
        onBlur={() => {
          if (local !== value) onSave(local);
        }}
        placeholder={placeholder}
        disabled={disabled}
        className={inputCls}
      />
    </label>
  );
}

function PersonField({
  label,
  hint,
  value,
  placeholder,
  disabled,
  onSave,
}: {
  label: string;
  hint?: string;
  value: string;
  placeholder?: string;
  disabled?: boolean;
  onSave: (v: string) => void;
}) {
  const [local, setLocal] = useState(value);
  useEffect(() => setLocal(value), [value]);
  return (
    <label className="block">
      <div className={labelCls}>
        {label}
        {hint && <span className="ml-2 normal-case text-ink-subtle font-normal">{hint}</span>}
      </div>
      <PersonPicker
        value={local}
        onChange={(v) => {
          setLocal(v);
          if (v !== value) onSave(v);
        }}
        placeholder={placeholder}
        disabled={disabled}
      />
    </label>
  );
}

function Textarea({
  label,
  hint,
  value,
  placeholder,
  disabled,
  rows = 3,
  onSave,
}: {
  label: string;
  hint?: string;
  value: string;
  placeholder?: string;
  disabled?: boolean;
  rows?: number;
  onSave: (v: string) => void;
}) {
  // Brief fields (Business Need, Expected Outcome, Risks, etc.) are
  // long-form narrative — exactly the surface that benefits most from
  // formatting. Backed by RichTextEditor; saves on blur, same as before.
  const [local, setLocal] = useState(value);
  useEffect(() => setLocal(value), [value]);
  // Locked briefs render as a plain RichText preview (no editor chrome).
  if (disabled) {
    return (
      <label className="block">
        <div className={labelCls}>
          {label}
          {hint && <span className="ml-2 normal-case text-ink-subtle font-normal">{hint}</span>}
        </div>
        {value.trim() ? (
          <div className={cn(inputCls, "min-h-[3rem] cursor-not-allowed bg-canvas")}>
            <RichText text={value} />
          </div>
        ) : (
          <div className={cn(inputCls, "min-h-[3rem] cursor-not-allowed bg-canvas italic text-ink-subtle")}>
            {placeholder || "—"}
          </div>
        )}
      </label>
    );
  }
  return (
    <div
      className="block"
      onBlur={(e) => {
        // Save when focus leaves the entire wrapper. Moving focus
        // between the textarea and toolbar buttons inside the editor
        // shouldn't trigger a save — currentTarget.contains(relatedTarget)
        // is true in those cases.
        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
          if (local !== value) onSave(local);
        }
      }}
    >
      <div className={labelCls}>
        {label}
        {hint && <span className="ml-2 normal-case text-ink-subtle font-normal">{hint}</span>}
      </div>
      <RichTextEditor
        value={local}
        onChange={setLocal}
        rows={rows}
        placeholder={placeholder}
        compact
      />
    </div>
  );
}

function SelectField({
  label,
  value,
  options,
  disabled,
  onSave,
}: {
  label: string;
  value: string;
  options: Array<{ value: string; label: string }>;
  disabled?: boolean;
  onSave: (v: string) => void;
}) {
  return (
    <label className="block">
      <div className={labelCls}>{label}</div>
      <select
        value={value}
        onChange={(e) => onSave(e.target.value)}
        disabled={disabled}
        className={inputCls}
      >
        <option value="">— select —</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function DateField({
  label,
  value,
  disabled,
  onSave,
}: {
  label: string;
  value: string;
  disabled?: boolean;
  onSave: (v: string) => void;
}) {
  return (
    <label className="block">
      <div className={labelCls}>{label}</div>
      <input
        type="date"
        value={value}
        onChange={(e) => onSave(e.target.value)}
        disabled={disabled}
        className={inputCls}
      />
    </label>
  );
}

function MoneyField({
  value,
  disabled,
  onSave,
}: {
  value: number | null;
  disabled?: boolean;
  onSave: (v: number | null) => void;
}) {
  const [local, setLocal] = useState(value?.toString() ?? "");
  useEffect(() => setLocal(value?.toString() ?? ""), [value]);
  return (
    <div className="relative">
      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-ink-subtle">$</span>
      <input
        type="number"
        min={0}
        step={1000}
        value={local}
        onChange={(e) => setLocal(e.target.value)}
        onBlur={() => {
          const parsed = local.trim() === "" ? null : parseFloat(local);
          const next = parsed !== null && !isNaN(parsed) ? parsed : null;
          if (next !== value) onSave(next);
        }}
        disabled={disabled}
        className={cn(inputCls, "pl-6 text-right tabular-nums")}
        placeholder="0"
      />
    </div>
  );
}

function formatMoney(amount: number): string {
  if (!amount) return "—";
  return `$${Math.round(amount).toLocaleString()}`;
}

// ---------------------------------------------------------------------------
// IT Checklist grid
// ---------------------------------------------------------------------------

function ITChecklist({
  selected,
  disabled,
  onChange,
}: {
  selected: string[];
  disabled?: boolean;
  onChange: (next: string[]) => void;
}) {
  const set = useMemo(() => new Set(selected), [selected]);

  const toggle = (key: string) => {
    const next = new Set(set);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onChange(Array.from(next));
  };

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
      {IT_CHECKLIST_CATEGORIES.map((cat) => (
        <div key={cat.category} className="rounded-lg border border-line-soft bg-canvas/40 p-3">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-ink-muted mb-2">
            {cat.category}
          </div>
          <div className="space-y-1.5">
            {cat.items.map((item) => (
              <label
                key={item.key}
                className={cn(
                  "flex items-start gap-2 text-xs text-ink-2 cursor-pointer",
                  disabled && "cursor-not-allowed opacity-60",
                )}
              >
                <input
                  type="checkbox"
                  checked={set.has(item.key)}
                  onChange={() => toggle(item.key)}
                  disabled={disabled}
                  className="mt-0.5 h-3.5 w-3.5 rounded border-line-strong text-brand-navy-600 dark:text-brand-navy-400 focus:ring-brand-navy-500"
                />
                <span className="flex-1">{item.label}</span>
              </label>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
